import React, { useEffect, useRef, useState } from "react";
import { Card, Computed, Slider } from "./shared";
import * as m from "../lib/model";
import { Building2, Repeat, GitMerge, Upload, CheckCircle2 } from "lucide-react";
import { useFinancialParse } from "../lib/useFinancialParse";
import { toast } from "sonner";

const PNL_FIELDS = ["sde", "ttmRevenue", "grossMarginPct", "historicalYears", "officerSalaryAddback", "employeeCount"];

function PlatformPnlUpload({ M }) {
  const { parseFile, busy } = useFinancialParse();
  const ref = useRef(null);
  const [last, setLast] = useState(null);
  const hy = M.historicalYears || [];
  const onFile = async (f) => {
    if (!f) return;
    try {
      const r = await parseFile(f, { only: PNL_FIELDS, renameDeal: false });
      setLast({ name: f.name, n: Object.keys(r.mapped).length });
      toast.success(`Platform P&L loaded — EBITDA ${m.fmt$(r.mapped.sde ?? M.sde)} · pro forma on Dashboard & Returns updated`);
    } catch { /* toast shown by hook */ }
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="pnl-upload" data-testid="recap-pnl-upload">
      <div className="pnl-txt">
        <b>Current platform P&L</b>
        <small>{last ? <><CheckCircle2 size={11} /> {last.name} · {last.n} fields applied</> : hy.length ? `${hy.length} historical year(s) on file · TTM revenue ${m.fmt$(M.ttmRevenue)}` : "Upload the latest P&L / financial statement — EBITDA, revenue, margin and history flow into the post-recap pro forma."}</small>
      </div>
      <button className="btn btn-sm" onClick={() => ref.current?.click()} disabled={busy} data-testid="recap-pnl-btn"><Upload size={12} />{busy ? "Parsing…" : "Upload P&L"}</button>
      <input ref={ref} type="file" accept=".pdf,.xlsx,.xls,.csv,.docx" style={{ display: "none" }} onChange={(e) => onFile(e.target.files?.[0])} data-testid="recap-pnl-input" />
    </div>
  );
}

const fmtIn = (n) => (Number(n) ? Math.round(n).toLocaleString("en-US") : "");
const parse = (s) => +String(s).replace(/[^\d]/g, "") || 0;

function Money({ label, value, onChange, testid, hint, readOnly }) {
  return (
    <div className="recap-field">
      <label>{label}{hint && <span className="blend-hint">{hint}</span>}</label>
      <div className="money-input ru-money" style={{ display: "flex" }}><span className="cur">$</span>
        <input className="inp mono" inputMode="numeric" placeholder="0" readOnly={readOnly} style={{ width: "100%", textAlign: "right", ...(readOnly ? { opacity: 0.85, cursor: "default" } : null) }} value={fmtIn(value)} onFocus={(e) => e.target.select()} onChange={(e) => !readOnly && onChange(parse(e.target.value))} data-testid={testid} /></div>
    </div>
  );
}

export function TxnTypeToggle({ M, patch }) {
  const recap = m.isRecap(M);
  const toAcquisition = () => patch({ txnType: "acquisition", ...(M.acqAskPrice ? { askPrice: M.acqAskPrice } : {}) });
  const toRecap = () => patch({
    txnType: "recap", acqAskPrice: M.askPrice,
    recap: { ...m.DEFAULT_RECAP, ...(M.recap || {}) },
    askPrice: Math.round((M.sde || 0) * (m.recapCfg(M).recapMult || 0)),
  });
  return (
    <div className="scen-bar cp-toggle" data-testid="txn-type-toggle">
      <button className={!recap ? "active" : ""} onClick={toAcquisition} data-testid="txn-acquisition"><Building2 size={13} /> Acquisition</button>
      <button className={recap ? "active" : ""} onClick={toRecap} data-testid="txn-recap"><Repeat size={13} /> Recapitalization</button>
    </div>
  );
}

const platformRow = (M) => ({ name: m.recapCfg(M).parentName || M.dealName || "Platform", ebitda: Math.round(M.sde || 0), multiple: Number(m.recapCfg(M).recapMult) || 0, year: 0 });
const rollupWithPlatform = (M) => {
  const RU = { ...m.DEFAULT_ROLLUP, ...(M.rollup || {}) };
  const cos = [...(RU.companies || [])];
  const i = cos.findIndex((c) => Number(c.year) === 0);
  if (i >= 0) cos[i] = { ...cos[i], ...platformRow(M) }; else cos.unshift(platformRow(M));
  return { ...RU, companies: cos };
};
const platformInSync = (M) => {
  const p = ((M.rollup || {}).companies || []).find((c) => Number(c.year) === 0);
  const want = platformRow(M);
  return !!p && p.name === want.name && Math.round(p.ebitda || 0) === want.ebitda && Math.abs((p.multiple || 0) - want.multiple) < 0.005;
};

export function RecapCard({ M, setField, patch, S, onRollUp }) {
  const R = m.recapCfg(M);
  const r = m.recapModel(M);
  const setR = (k, v) => patch({ recap: { ...R, [k]: v } });
  const synced = platformInSync(M);
  const warNeed = Math.round(m.addonEquityNeed(M));
  const addons = ((M.rollup || {}).companies || []).filter((c) => Number(c.year) > 0).length;
  const pushToRollup = () => patch({ rollup: rollupWithPlatform(M) });
  // The war chest is always the cash we are raising for the add-ons — keep it locked to that number.
  useEffect(() => {
    if (Math.abs(warNeed - (Number(R.warChest) || 0)) > 0.5) setR("warChest", warNeed);
  }, [warNeed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const ev = Math.round((M.sde || 0) * (Number(R.recapMult) || 0));
    if (Math.abs(ev - (M.askPrice || 0)) > 0.5) setField("askPrice", ev);
  }, [M.sde, R.recapMult]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (R.syncRollup && !platformInSync(M)) patch({ rollup: rollupWithPlatform(M) });
  }, [M.sde, R.recapMult, R.parentName, R.syncRollup]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Card title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Repeat size={13} />1 · Recapitalization{R.parentName ? <span className="ra-sub">· {R.parentName}</span> : null}</span>} testid="recap-card">
      <S label="Platform EBITDA (TTM)" k="sde" min={0} max={10000000} step={10000} format={m.fmt$} testid="s-sde" />
      <S label="Platform TTM revenue" k="ttmRevenue" min={0} max={50000000} step={50000} format={m.fmt$} testid="s-ttmRevenue" />
      <S label="Gross margin %" k="grossMarginPct" min={0} max={90} step={0.5} format={(v) => `${v}%`} testid="s-grossMarginPct" />
      <Computed label="EBITDA margin on revenue" value={M.ttmRevenue ? m.fmtPct(((M.sde || 0) / M.ttmRevenue) * 100) : "—"} />
      <PlatformPnlUpload M={M} />
      <Slider label="Recap valuation multiple" value={Number(R.recapMult) || 0} onChange={(v) => setR("recapMult", v)} min={2} max={12} step={0.25} format={(v) => `${v}×`} testid="s-recapMult" />
      <Computed label="Pre-money enterprise value" value={m.fmt$(r.ev)} />
      <div className="recap-grid">
        <Money label="Existing debt to refinance" value={r.existingDebt} onChange={(v) => setR("existingDebt", v)} testid="recap-existingDebt" />
        <Money label="New senior facility" value={r.newDebt} onChange={(v) => setR("newDebt", v)} testid="recap-newDebt" hint={`${r.leverage.toFixed(2)}× EBITDA`} />
        <Money label="Add-on war chest" value={warNeed} onChange={() => {}} readOnly testid="recap-warChest"
          hint={warNeed > 0 ? `auto — matches ${addons} add-on${addons === 1 ? "" : "s"} equity` : "add add-ons in Assumptions → Roll-Up"} />
      </div>
      <S label="New facility rate %" k="rate" min={4} max={16} step={0.25} format={(v) => `${v}%`} testid="s-rate" />
      <S label="New facility term (yrs)" k="loanYrs" min={3} max={25} step={1} format={(v) => `${v} yr`} testid="s-loanYrs" />
      <Slider label="Legacy equity cashed out %" value={Number(R.cashOutPct) || 0} onChange={(v) => setR("cashOutPct", v)} min={0} max={100} step={5} format={(v) => `${v}%`} testid="s-cashOutPct" />
      <Computed label="Legacy equity value (EV − existing debt)" value={m.fmt$(r.legacyEquityValue)} />
      <Computed label="Cash-out to legacy investors" value={m.fmt$(r.cashOut)} />
      <Computed label="Rolled legacy equity" value={m.fmt$(r.rolled)} />
      <Computed label="New LP equity required" value={m.fmt$(r.newLp)} tone={r.newLp > 0 ? "go" : undefined} />
      {r.excess > 0.5 && <Computed label="Excess proceeds → balance sheet" value={m.fmt$(r.excess)} tone="go" />}
      <Computed label="New LPs' share of common" value={m.fmtPct(r.newLpOwnership * 100)} />
      <div className="recap-rollup" data-testid="recap-rollup">
        <label className="cp-check"><input type="checkbox" checked={!!R.syncRollup} onChange={(e) => setR("syncRollup", e.target.checked)} data-testid="recap-sync-rollup" /><span><GitMerge size={12} style={{ marginRight: 5, verticalAlign: -2 }} />Add this platform to the Roll-Up as the Year 0 company and keep it in sync (EBITDA {m.fmt$(M.sde || 0)} · {m.fmtX(Number(R.recapMult) || 0)} entry)</span></label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className={`tag ${synced ? "" : "warn"}`} data-testid="recap-rollup-status">{synced ? "In Roll-Up" : "Not in Roll-Up"}</span>
          {!synced && <button className="btn btn-sm" onClick={pushToRollup} data-testid="recap-push-rollup">Add to Roll-Up now</button>}
          {onRollUp && <button className="link-btn lime" onClick={onRollUp} data-testid="recap-open-rollup">Open Roll-Up →</button>}
        </div>
      </div>
      <div className="section-label" style={{ marginTop: 12 }}>Legacy investors (original deal)</div>
      <div className="recap-grid">
        <Money label="Original equity invested" value={r.orig} onChange={(v) => setR("origEquity", v)} testid="recap-origEquity" />
        <div className="recap-field"><label>Years since original close</label><input className="inp mono" type="number" min={1} max={30} step={1} value={R.origYears} onChange={(e) => setR("origYears", +e.target.value || 1)} data-testid="recap-origYears" /></div>
        <Money label="Distributions received to date" value={r.dist} onChange={(v) => setR("legacyDist", v)} testid="recap-legacyDist" />
      </div>
      <Computed label="Realized MOIC (cash-out + distributions)" value={m.fmtX(r.moicRealized)} tone={r.moicRealized >= 1 ? "go" : "pass"} />
      <Computed label="Total MOIC incl. rolled equity" value={m.fmtX(r.moicTotal)} tone={r.moicTotal >= 2 ? "go" : undefined} />
      <Computed label={`IRR since close (${r.yrs} yrs, incl. roll)`} value={isNaN(r.irrTotal) ? "n/a" : m.fmtPct(r.irrTotal)} tone={r.irrTotal >= 20 ? "go" : undefined} />
    </Card>
  );
}

export function LegacyReturnsCard({ M, testid = "legacy-returns", compact = false }) {
  const r = m.recapModel(M);
  return (
    <Card title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Repeat size={13} />Legacy Investors — Realized at Recap{r.R.parentName ? <span className="ra-sub">· {r.R.parentName}</span> : null}</span>} testid={testid}>
      <div className={`grid ${compact ? "g3" : "g4"}`}>
        <div className="tile" data-testid={`${testid}-cashout`}><div className="label">Cash-out to legacy LPs</div><div className="val pos">{m.fmt$(r.cashOut)}</div><div className="sub">{r.R.cashOutPct}% of {m.fmt$(r.legacyEquityValue)} equity value</div></div>
        <div className="tile" data-testid={`${testid}-rolled`}><div className="label">Rolled into new vehicle</div><div className="val">{m.fmt$(r.rolled)}</div><div className="sub">at {m.fmtX(r.R.recapMult)} recap mark</div></div>
        <div className="tile" data-testid={`${testid}-moic`}><div className="label">Realized MOIC / total</div><div className="val pos">{m.fmtX(r.moicRealized)} <span style={{ fontSize: 13, color: "var(--text2)" }}>/ {m.fmtX(r.moicTotal)}</span></div><div className="sub">on {m.fmt$(r.orig)} original equity</div></div>
        {!compact && <div className="tile" data-testid={`${testid}-irr`}><div className="label">IRR since close</div><div className="val pos">{isNaN(r.irrTotal) ? "n/a" : m.fmtPct(r.irrTotal)}</div><div className="sub">{r.yrs} yrs · realized-only {isNaN(r.irrRealized) ? "n/a" : m.fmtPct(r.irrRealized)}</div></div>}
      </div>
      <table className="tbl" style={{ marginTop: 10 }}>
        <tbody>
          <tr><td>Original equity invested ({r.yrs} yrs ago)</td><td className="num">{m.fmt$(r.orig)}</td></tr>
          <tr><td>Distributions received to date</td><td className="num pos">{m.fmt$(r.dist)}</td></tr>
          <tr><td>Cash-out at recap</td><td className="num pos">{m.fmt$(r.cashOut)}</td></tr>
          <tr className="hl"><td><b>Realized proceeds</b></td><td className="num"><b>{m.fmt$(r.realized)}</b></td></tr>
          <tr><td>Rolled equity (marked at recap valuation)</td><td className="num">{m.fmt$(r.rolled)}</td></tr>
          <tr className="total"><td>Total value to legacy investors</td><td className="num">{m.fmt$(r.totalValue)}</td></tr>
        </tbody>
      </table>
      <div className="ir-fine">Legacy investors receive {m.fmt$(r.cashOut)} in cash and keep {m.fmt$(r.rolled)} of rolled equity, which converts into the new vehicle alongside {m.fmt$(r.newLp)} of new LP equity ({m.fmtPct(r.newLpOwnership * 100)} to new LPs). Go-forward returns are modeled on total post-recap equity — new LP equity plus rolled legacy equity ({m.fmt$(r.newLp + r.rolled)}) — entering at the {m.fmtX(r.R.recapMult)} recap valuation.</div>
    </Card>
  );
}
